import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo, useRef, useState } from "react";
import CustomDataGridV2 from "../../../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../../../components/Flex";
import RenderWithTooltip from "../../../../../../../../../components/RenderWithTooltip";
import Text from "../../../../../../../../../components/Text";
import useDisclosure from "../../../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../../../hooks/use-query-param";
import { useGetFormsAndAssessmentsByAppointment } from "../../../../../../../../../queries/patient-chart/forms/use-get-forms-and-assessments-by-appointment";
import {
  applyDefaultColumnSettings,
  getXtenantId,
} from "../../../../../../../../../utils/utils";
import { INTAKE_FORM_SECTIONS_MAPPER } from "../../../../../../scheduling/appointment/appointment-constant";
import IntakePreviewModal from "../../../../../../scheduling/appointment/encounter/content-section/intake-preview/IntakePreviewModal";
import { formatDate } from "../../../../../../../../../utils/date-time-utility";
import { GlobalTableHeightWrapper } from "../../../../../../../../../components/GlobalTableHeightWrapper";
import ViewAssessmentDrawer from "./ViewAssessmentDrawer";

type FormFilters = {
  intakeForms: boolean;
  patientAssessments: boolean;
};

const FILTER_CHECKBOXES: { key: keyof FormFilters; label: string }[] = [
  { key: "intakeForms", label: "Intake Forms" },
  { key: "patientAssessments", label: "Patient Assessments" },
];

// Form names arrive as internal tokens (e.g. "physicianPatientArbitrationAgreement").
// Prefer the canonical label from INTAKE_FORM_SECTIONS; otherwise split
// camelCase/underscores into spaced Title Case so nothing internal is exposed.
const formatFormName = (name?: string): string => {
  if (!name) return "-";
  return (
    INTAKE_FORM_SECTIONS_MAPPER[name] ??
    name
      .replace(/_/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/(?:^|\s)(\w)/g, (match) => match.toUpperCase())
  );
};

const FormsAndAssessmentTab = () => {
  const appointmentId = useQueryParams("appointmentid");
  const [filters, setFilters] = useState<FormFilters>({
    intakeForms: true,
    patientAssessments: false,
  });
  const previewIntakeDisc = useDisclosure();
  const [selectedScreenerType, setSelectedScreenerType] = useState<string>("");
  const assessmentPdfRef = useRef<{ generatePdf: () => void }>(null);

  const handleFilterChange = (key: keyof FormFilters, checked: boolean) => {
    setFilters((prev) => ({ ...prev, [key]: checked }));
  };

  const { data: formsAndAssessments, isLoading } =
    useGetFormsAndAssessmentsByAppointment({
      appointmentUuid: appointmentId ?? "",
      xTENANTID: getXtenantId() ?? "",
    });

  const rows = useMemo(() => {
    const apiData = (formsAndAssessments as any)?.data?.data;
    const patientForms: any[] = apiData?.patientForms ?? [];
    const assessments: any[] = apiData?.assessments ?? [];

    const formsWithType = (filters.intakeForms ? patientForms : []).map(
      (item) => ({ ...item, type: "INTAKE_FORM" as const })
    );
    const assessmentsWithType = (
      filters.patientAssessments ? assessments : []
    ).map((item) => ({ ...item, type: "ASSESSMENT" as const }));

    return [...formsWithType, ...assessmentsWithType].map((item, index) => ({
      uuid: item.uuid,
      srNo: index + 1,
      documentType: item.name,
      details: item.status,
      type: item.type,
      content: item.content,
      patientId: item.patientId,
      appointmentId: item.appointmentId,
      screenerType: item.name,
      createdOn: item.sendOn ? formatDate(item.sendOn) : "-",
    }));
  }, [formsAndAssessments, filters]);

  const COLUMNS: GridColDef[] = applyDefaultColumnSettings(
    [
      {
        field: "srNo",
        headerName: "Sr. No",
        flex: 0.3,
        minWidth: 80,
        sortable: false,
        renderCell: ({ value }) => String(value).padStart(2, "0"),
      },
      {
        field: "type",
        headerName: "Document Type",
        flex: 0.6,
        minWidth: 150,
        sortable: false,
        renderCell: ({ value }) => {
          const DOCUMENT_TYPE_LABELS: Record<string, string> = {
            INTAKE_FORM: "Intake Form",
            ASSESSMENT: "Assessment",
          };
          return <Text isTabletext>{DOCUMENT_TYPE_LABELS[value] ?? "-"}</Text>;
        },
      },
      {
        field: "documentType",
        headerName: "Details",
        flex: 0.5,
        minWidth: 120,
        sortable: false,
        renderCell: ({ value }) => (
          <RenderWithTooltip value={formatFormName(value)} />
        ),
      },
      {
        field: "createdOn",
        headerName: "Created On",
        flex: 0.6,
        minWidth: 140,
        sortable: false,
      },
      {
        field: "action",
        headerName: "Action",
        flex: 0.4,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }) => (
          <Text
            isTabletext
            sx={{
              color: "#104E84",
              fontWeight: 500,
              cursor: "pointer",
            }}
            onClick={() => {
              if (row.type === "INTAKE_FORM") {
                previewIntakeDisc.open({ section: row.documentType });
              } else if (row.type === "ASSESSMENT") {
                setSelectedScreenerType(row.screenerType);
              }
            }}
          >
            View
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <Box display="flex" flexDirection="column" flex={1} bgcolor="white" p={1.5}>
      <ViewAssessmentDrawer
        pdfRef={assessmentPdfRef}
        screenerType={selectedScreenerType}
        appointmentId={appointmentId ?? ""}
        onClose={() => setSelectedScreenerType("")}
      />
      <IntakePreviewModal
        disclosureHost={previewIntakeDisc}
        selectedSection={previewIntakeDisc?.data?.section}
      />
      <Flex gap={2} mb={1.5}>
        {FILTER_CHECKBOXES.map(({ key, label }) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={filters[key]}
                onChange={(e) => handleFilterChange(key, e.target.checked)}
                size="small"
              />
            }
            label={label}
            slotProps={{ typography: { fontSize: "14px", fontWeight: 500 } }}
          />
        ))}
      </Flex>

      <GlobalTableHeightWrapper
        height="calc(100vh -  20rem)"
        sx={{ overflowY: "auto" }}
      >
        <CustomDataGridV2
          getRowId={(row) => row.uuid}
          rows={rows}
          columns={COLUMNS}
          rowCount={rows.length}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooterPagination
          hideFooter
          loading={isLoading}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default FormsAndAssessmentTab;
